import React from 'react';
import styled from '@emotion/styled';

import { getColor } from './UICommon';
import { TitleAndDescriptionProps } from './BaseStep';
import { StepType } from '../../models/types/step.types';
import { QuoteCard } from './QuoteCard';

import highlightWave from '../../assets/highlightWave.svg';
import purpleArrow from '../../assets/purpleArrow.svg';

interface StyleProps {
  isButton?: boolean;
  forBaseStepV2?: boolean;
  titleSpace?: boolean;
}

const H3 = styled.h3`
  color: ${getColor('BLACK')};
  font-size: 28px;
  font-family: 'Lora';
  font-weight: 600;
  line-height: 32px;
  -webkit-font-smoothing: antialiased;
  ${(props: StyleProps) => {
    if (props.titleSpace) {
      return 'padding-top: 20px;';
    }
  }};
`;

const Div = styled.div`
  position: relative;
  font-size: 18px;
  font-weight: 400;
  padding: 0px;
  color: ${getColor('BLACK_60')};
  line-height: 24px;
  ${(props: StyleProps) => {
    if (props.forBaseStepV2) {
      return 'padding: 0px 0px 32px 0px;';
    }

    return `padding: ${props.isButton ? '24px 0 16px' : '32px 0 24px'}`;
  }};
`;

const Span = styled.span`
  cursor: pointer;
  color: ${getColor('PURPLE_100')};
  text-decoration: none;
  :focus-visible {
    outline: none;
  }
`;

const ColoredP = styled.p`
  color: ${getColor('PURPLE_60')};
  font-weight: 500;
`;

const WaveImg = styled.img`
  position: absolute;
  bottom: -5px;
  width: 100%;
  z-index: -1;
`;

const ArrowImg = styled.img`
  position: absolute;
  left: -42px;
  top: 74px;
`;

const SvgBehindHighlight = ({ title, highlight }: { title: string; highlight: string }) => {
  const highlightIndex = title.indexOf(highlight);
  const beforeHighlight = title.substring(0, highlightIndex);
  const afterHighlight = title.substring(highlightIndex + highlight.length);

  return (
    <div>
      {beforeHighlight}
      <span style={{ position: 'relative' }}>
        <WaveImg src={highlightWave} alt='highlight-wave' />
        <span> {highlight}</span>
      </span>
      {afterHighlight}
    </div>
  );
};

export interface MainTitleDescriptionProps extends TitleAndDescriptionProps {
  isButton?: boolean;
  stepType: StepType;
  mainTitleImageUrl?: string;
  mainTitleImageBackgroundSize?: string;
  mainTitleImageHeight?: string;
  forBaseStepV2?: boolean;
  extraSpace?: boolean;
}

export const MainTitleDescription = ({
  title,
  description,
  isButton = false,
  stepType,
  mainTitleImageUrl,
  mainTitleImageBackgroundSize,
  mainTitleImageHeight,
  highlight,
  isColoredDescription,
  isTitleArrow,
  extraSpace,
  forBaseStepV2 = false,
}: MainTitleDescriptionProps) => {
  const showDescription = () => {
    if (!!description) {
      if (stepType === StepType.SinglePagePickAGroup) {
        return (
          <p>
            {description}{' '}
            <Span
              onClick={() =>
                (window.location.href = `${process.env.REACT_APP_WEBSITE}/onboarding/document/discovery-repeat/${window.location.search}`)
              }
            >
              click here
            </Span>
          </p>
        );
      }

      return isColoredDescription ? <ColoredP>{description}</ColoredP> : <p>{description}</p>;
    }

    return null;
  };

  return (
    <Div isButton={isButton} forBaseStepV2={forBaseStepV2}>
      {mainTitleImageUrl && (
        <>
          <QuoteCard
            imageUrl={mainTitleImageUrl}
            imageBackgroundSize={mainTitleImageBackgroundSize}
            imageHeight={mainTitleImageHeight}
          />
          <Div isButton={true}></Div>
        </>
      )}
      {isTitleArrow && <ArrowImg src={purpleArrow} alt='purple-arrow' />}
      <H3 titleSpace={extraSpace}>
        {!!highlight && !!title ? <SvgBehindHighlight title={title as string} highlight={highlight} /> : title}
      </H3>
      {showDescription()}
    </Div>
  );
};
